import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import berliLogo from "../../assets/logo1.svg";

/* ------------------------------------------------------------------
   SpecialPopup — Papon Live in Concert, powered by Berli Brewing Co.

   One component, two ways to use it:

     <SpecialPopup />                 -> popup, opens 5s after page load
     <SpecialPopup mode="section" />  -> normal section inside the page

   The popup opens 5 seconds after every page load.
   To show it only once per browser session instead, use:
     <SpecialPopup showOncePerSession />

   Media (served from the VPS):
     imageSrc   -> LEFT  : 1080 x 1350 (4:5)  image
     videoSrc   -> RIGHT : 1080 x 1920 (9:16) video (mp4 / webm)
     videoPoster (optional) -> thumbnail shown while the video loads
------------------------------------------------------------------- */

/* Ticket links — replace these with the real event URLs */
const DISTRICT_TICKET_URL = "https://www.district.in/events/papon-live-in-concert-pune-2026-buy-tickets";
const BOOKMYSHOW_TICKET_URL = "https://in.bookmyshow.com/events/papon-live-in-concert-pune/ET00448303";

const DEFAULTS = {
  imageSrc: "/media/popup/papon-poster.png",
  imageAlt: "Papon Live in Concert poster, powered by Berli Brewing Co.",
  videoSrc: "/media/popup/papon-reel.mp4",
  videoAlt: "Papon performing live",
};

/* Inject the stylesheet once, even if the popup and the section are both on the page */
const STYLE_ID = "sp-papon-styles";
const SEEN_KEY = "sp-papon-seen-v2";
const useSpStyles = () => {
  useLayoutEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = styles;
    document.head.appendChild(el);
  }, []);
};

let idCounter = 0;
const useStableId = (prefix) => {
  const ref = useRef(null);
  if (ref.current === null) ref.current = `${prefix}-${++idCounter}`;
  return ref.current;
};

/* ---------------- Media ---------------- */

const Media = ({ src, type, poster, alt, muted }) => {
  const videoRef = useRef(null);

  // React doesn't reliably reflect `muted`, which breaks autoplay on iOS.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    v.defaultMuted = true;
  }, [muted, src]);

  // Play only while the video is visible (saves data when used as a page section
  // and pauses when swiped away in the mobile carousel).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      const p = v.play?.();
      if (p && p.catch) p.catch(() => { });
    };
    if (!("IntersectionObserver" in window)) {
      tryPlay();
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? tryPlay() : v.pause()),
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [src]);

  if (!src) return <div className="sp-empty">Add a {type} src for this slot</div>;

  return type === "video" ? (
    <video
      ref={videoRef}
      className="sp-media-el"
      src={src}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      aria-label={alt}
    />
  ) : (
    <img className="sp-media-el" src={src} alt={alt} loading="eager" />
  );
};

/* ---------------- Shared content (used by both modes) ---------------- */

const ShowcaseContent = ({
  titleId,
  imageSrc,
  imageAlt,
  videoSrc,
  videoPoster,
  videoAlt,
  onClose,
  closeBtnRef,
}) => {
  const [muted, setMuted] = useState(true);
  const [slide, setSlide] = useState(0);
  const rowRef = useRef(null);

  const onRowScroll = () => {
    const el = rowRef.current;
    if (!el || !el.clientWidth) return;
    setSlide(Math.round(el.scrollLeft / el.clientWidth));
  };

  const goTo = (i) => {
    const el = rowRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <>
      <div className="sp-stagelight" aria-hidden="true" />
      <div className="sp-grain" aria-hidden="true" />

      {onClose && (
        <button ref={closeBtnRef} className="sp-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}

      <header className="sp-head">
        <img className="sp-logo" src={berliLogo} alt="Berli Brewing Co." />
        <div className="sp-head-text">
          <h2 id={titleId} className="sp-title">
            Papon, live in concert
          </h2>
        </div>
      </header>

      <div className="sp-row" ref={rowRef} onScroll={onRowScroll}>
        <figure className="sp-tile">
          <div className="sp-frame sp-frame--poster">
            <Media src={imageSrc} type="image" alt={imageAlt} />
          </div>
        </figure>

        <figure className="sp-tile">
          <div className="sp-frame sp-frame--story">
            <Media src={videoSrc} type="video" poster={videoPoster} alt={videoAlt} muted={muted} />
            {videoSrc && (
              <button
                className="sp-mute"
                onClick={() => setMuted((m) => !m)}
                aria-label={muted ? "Turn sound on" : "Turn sound off"}
              >
                {muted ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
                    <path d="M17 9l4 6M21 9l-4 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
                    <path d="M16 8.5a5 5 0 010 7M18.5 6a8.5 8.5 0 010 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </figure>
      </div>

      <div className="sp-dots" role="tablist" aria-label="Choose slide">
        {[0, 1].map((i) => (
          <button
            key={i}
            role="tab"
            aria-selected={slide === i}
            aria-label={i === 0 ? "Poster" : "Video"}
            className={`sp-dot ${slide === i ? "is-active" : ""}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>

      <footer className="sp-foot">
        <div className="sp-when">
          <p className="sp-date">10th October, Saturday, 7 PM</p>
          <p className="sp-venue">Oasis Lawn, Amanora Mall Pune</p>
        </div>
        <div className="sp-tickets">
          <a
            className="sp-ticket sp-ticket--primary"
            href={DISTRICT_TICKET_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get tickets on District
          </a>
          <a
            className="sp-ticket"
            href={BOOKMYSHOW_TICKET_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Book on BookMyShow
          </a>
        </div>
        <p className="sp-sponsor">
          Powered By        <strong>Berli Brewing Company</strong>
        </p>
      </footer>
    </>
  );
};

/* ---------------- Section mode ---------------- */

const SectionView = ({ id, ...media }) => {
  const titleId = useStableId("sp-section-title");
  return (
    <section id={id} className="sp-root sp-section" aria-labelledby={titleId}>
      <div className="sp-card sp-card--section">
        <ShowcaseContent titleId={titleId} {...media} />
      </div>
    </section>
  );
};

/* ---------------- Popup mode ---------------- */

const PopupView = ({ delay, showOncePerSession, ...media }) => {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeBtnRef = useRef(null);
  const lastFocus = useRef(null);
  const titleId = useStableId("sp-popup-title");

  useEffect(() => {
    let seen = false;
    try {
      seen = showOncePerSession && sessionStorage.getItem(SEEN_KEY) === "1";
    } catch (e) { }
    if (seen) return;
    const t = setTimeout(() => {
      lastFocus.current = document.activeElement;
      setOpen(true);
    }, delay);
    return () => clearTimeout(t);
  }, [delay, showOncePerSession]);

  const close = useCallback(() => {
    setClosing(true);
    try {
      if (showOncePerSession) sessionStorage.setItem(SEEN_KEY, "1");
    } catch (e) { }
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
      lastFocus.current?.focus?.();
    }, 380);
  }, [showOncePerSession]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  if (!open) return null;

  // Render straight into <body> so page wrappers (transforms, animations,
  // overflow) can never hide or misplace the popup.
  return createPortal(
    <div
      className={`sp-root sp-overlay ${closing ? "is-closing" : ""}`}
      onMouseDown={(e) => e.target === e.currentTarget && close()}
    >
      <div className="sp-card sp-card--popup" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <ShowcaseContent titleId={titleId} onClose={close} closeBtnRef={closeBtnRef} {...media} />
      </div>
    </div>,
    document.body
  );
};

/* ---------------- Public component ---------------- */

const SpecialPopup = ({
  mode = "popup", // "popup" | "section"
  id, // optional anchor id for section mode, e.g. "papon-concert"
  imageSrc = DEFAULTS.imageSrc,
  imageAlt = DEFAULTS.imageAlt,
  videoSrc = DEFAULTS.videoSrc,
  videoPoster,
  videoAlt = DEFAULTS.videoAlt,
  delay = 5000,
  showOncePerSession = false, // true = show the popup only once per browser session
}) => {
  useSpStyles();
  const media = { imageSrc, imageAlt, videoSrc, videoPoster, videoAlt };

  return mode === "section" ? (
    <SectionView id={id} {...media} />
  ) : (
    <PopupView delay={delay} showOncePerSession={showOncePerSession} {...media} />
  );
};

export default SpecialPopup;

/* ------------------------------------------------------------------ */

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@400;600&family=Playfair+Display:wght@600;800&display=swap');

/* ---------- shared tokens ---------- */
.sp-root{
  --sp-night:#12172a;       /* stage sky */
  --sp-smoke:#262c44;       /* haze */
  --sp-crimson:#c8141f;     /* stage light */
  --sp-ember:#6e0b13;       /* crowd glow */
  --sp-gold:#e9c84a;        /* Berli gold */
  --sp-chrome:#f3efe8;      /* title chrome */
  --sp-muted:#b9b4c4;
  font-family:'Josefin Sans', 'Trebuchet MS', system-ui, sans-serif;
  color:var(--sp-chrome);
}
.sp-root *, .sp-root *::before, .sp-root *::after{ box-sizing:border-box; }

/* ---------- popup shell ---------- */
.sp-overlay{
  --sp-h: min(calc(100dvh - 365px), 620px, calc((100vw - 160px) / 1.3625));
  position:fixed; inset:0; z-index:9999;
  display:flex; align-items:center; justify-content:center;
  padding:24px;
  background:
    radial-gradient(ellipse at 50% 110%, rgba(200,20,31,.35), transparent 60%),
    rgba(8,10,20,.78);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: sp-fade .4s ease both;
}
.sp-overlay.is-closing{ animation: sp-fade-out .38s ease both; }
.sp-overlay.is-closing .sp-card{ animation: sp-drop .38s ease both; }

/* ---------- section shell ---------- */
.sp-section{
  --sp-h: min(600px, calc((min(100vw, 1280px) - 520px) / 1.3625));
  padding: clamp(40px, 6vw, 88px) 20px;
}

/* ---------- card ---------- */
.sp-card{
  position:relative;
  padding:28px 32px 26px;
  border-radius:20px;
  background:linear-gradient(180deg, var(--sp-night) 0%, var(--sp-smoke) 48%, var(--sp-ember) 100%);
  box-shadow:
    0 0 0 1px rgba(243,239,232,.08),
    0 30px 80px rgba(0,0,0,.45),
    0 0 120px rgba(200,20,31,.2);
  isolation:isolate;
  overflow:hidden;
}
.sp-card--popup{
  max-height:calc(100dvh - 48px);
  overflow:auto;
  overscroll-behavior:contain;
  scrollbar-width:none;
  animation: sp-rise .6s cubic-bezier(.2,.8,.2,1) both;
}
.sp-card--popup::-webkit-scrollbar{ display:none; }

.sp-card--section{
  max-width:1280px;
  margin:0 auto;
  padding:48px;
  border-radius:28px;
}

/* stage light — the red panel behind Papon on the poster */
.sp-stagelight{
  position:absolute; z-index:-1;
  top:-10%; left:38%;
  width:28%; height:70%;
  background:linear-gradient(180deg, rgba(200,20,31,.55), rgba(200,20,31,0));
  filter:blur(40px);
  transform:skewX(-8deg);
  pointer-events:none;
}
.sp-grain{
  position:absolute; inset:0; z-index:-1; pointer-events:none; opacity:.18; mix-blend-mode:overlay;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

.sp-close{
  position:absolute; top:14px; right:14px; z-index:3;
  width:40px; height:40px; border-radius:50%;
  display:grid; place-items:center;
  border:1px solid rgba(243,239,232,.25);
  background:rgba(18,23,42,.6);
  color:var(--sp-chrome); cursor:pointer;
  transition:background .2s, transform .2s;
}
.sp-close:hover{ background:var(--sp-crimson); transform:rotate(90deg); }

/* ---------- header ---------- */
.sp-head{
  display:flex; align-items:center; gap:20px;
  margin:0 56px 22px 0;
}
.sp-logo{
  display:block; flex:none;
  width:140px; height:auto;
}
.sp-head-text{ min-width:0; }
.sp-title{
  margin:0;
  font-family:'Playfair Display', Georgia, serif; font-weight:800;
  font-size:clamp(26px, 3.2vw, 40px); line-height:1.05; letter-spacing:-.01em;
  background:linear-gradient(180deg, #ffffff 0%, #d9d6d0 55%, #9d9a95 100%);
  -webkit-background-clip:text; background-clip:text; color:transparent;
}

/* ---------- media ---------- */
.sp-row{
  display:flex; align-items:flex-end; justify-content:center; gap:20px;
}
.sp-tile{ margin:0; }
.sp-frame{
  position:relative; height:var(--sp-h);
  border-radius:14px; overflow:hidden;
  background:#0a0d18;
  box-shadow:0 0 0 1px rgba(243,239,232,.12), 0 18px 40px rgba(0,0,0,.5);
}
.sp-frame--poster{ aspect-ratio:1080/1350; }
.sp-frame--story{
  aspect-ratio:1080/1920;
  box-shadow:0 0 0 1px rgba(233,200,74,.35), 0 18px 40px rgba(0,0,0,.5), 0 0 50px rgba(200,20,31,.35);
}
.sp-media-el{ display:block; width:100%; height:100%; object-fit:cover; }
.sp-empty{
  height:100%; display:grid; place-items:center; padding:16px; text-align:center;
  color:var(--sp-muted); font-size:14px;
  border:1px dashed rgba(243,239,232,.25); border-radius:14px;
}
.sp-mute{
  position:absolute; right:10px; bottom:10px;
  width:38px; height:38px; border-radius:50%;
  display:grid; place-items:center; cursor:pointer;
  border:1px solid rgba(243,239,232,.3);
  background:rgba(18,23,42,.65); color:var(--sp-chrome);
}
.sp-mute:hover{ background:var(--sp-crimson); }

.sp-dots{ display:none; }

/* ---------- footer ---------- */
.sp-foot{
  display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:18px 20px;
  margin-top:22px; padding-top:18px;
  border-top:1px solid rgba(243,239,232,.14);
}
/* ticket buttons — own full-width row under the date / sponsor line */
.sp-tickets{
  order:3; width:100%;
  display:flex; gap:12px;
}
.sp-ticket{
  flex:1 1 0; min-width:0;
  display:inline-flex; align-items:center; justify-content:center;
  min-height:46px; padding:10px 18px; border-radius:999px;
  border:1px solid rgba(243,239,232,.4);
  background:rgba(18,23,42,.45);
  color:var(--sp-chrome); text-decoration:none; text-align:center;
  font-size:15px; font-weight:600; line-height:1.2; letter-spacing:.02em;
  transition:background .2s, border-color .2s, color .2s, transform .2s;
}
.sp-ticket:hover{ background:var(--sp-crimson); border-color:var(--sp-crimson); transform:translateY(-1px); }
.sp-ticket--primary{
  background:var(--sp-gold); border-color:var(--sp-gold); color:var(--sp-night);
}
.sp-ticket--primary:hover{ background:#f3d867; border-color:#f3d867; color:var(--sp-night); }
.sp-date{
  margin:0; font-family:'Playfair Display', Georgia, serif; font-weight:800;
  font-size:clamp(18px, 1.8vw, 24px); letter-spacing:-.01em;
}
.sp-venue{ margin:4px 0 0; font-size:16px; color:var(--sp-muted); }
.sp-sponsor{
  margin:0; text-align:right; font-size:14px; color:var(--sp-muted);
  display:flex; flex-direction:column; gap:2px;
}
.sp-sponsor strong{
  font-family:'Playfair Display', Georgia, serif; font-weight:800;
  font-size:20px; color:var(--sp-gold); letter-spacing:.01em;
}

.sp-root :focus-visible{ outline:2px solid var(--sp-gold); outline-offset:3px; }

/* ---------- section: desktop layout (text left, media right) ---------- */
@media (min-width: 1025px){
  .sp-card--section{
    display:grid;
    grid-template-columns:minmax(0, 1fr) auto;
    grid-template-areas:
      "head media"
      "foot media";
    column-gap:56px;
  }
  .sp-card--section .sp-head{
    grid-area:head; align-self:end;
    flex-direction:column; align-items:flex-start; gap:22px;
    margin:0;
  }
  .sp-card--section .sp-logo{ width:170px; }
  .sp-card--section .sp-title{ font-size:clamp(36px, 3.6vw, 54px); }
  .sp-card--section .sp-row{ grid-area:media; }
  .sp-card--section .sp-tickets{ order:0; }
  .sp-card--section .sp-foot{
    grid-area:foot; align-self:start;
    flex-direction:column; align-items:flex-start; gap:22px;
    margin-top:32px; padding-top:24px;
  }
  .sp-card--section .sp-sponsor{ text-align:left; }
  .sp-card--section .sp-stagelight{ left:52%; }
}

/* ---------- tablet ---------- */
@media (max-width: 1024px){
  .sp-card{ padding:24px 22px 22px; }
  .sp-overlay{ --sp-h: min(calc(100dvh - 405px), 560px, calc((100vw - 110px) / 1.3625)); }
  .sp-tickets{ order:0; }
  .sp-section{ --sp-h: min(62vh, 580px, calc((100vw - 110px) / 1.3625)); }
  .sp-card--section .sp-head{ margin-right:0; }
  .sp-foot{ flex-direction:column; align-items:flex-start; }
  .sp-sponsor{ text-align:left; }
}

/* ---------- mobile: logo stacked above text, swipeable carousel ---------- */
@media (max-width: 767px){
  .sp-overlay{ padding:12px; align-items:flex-end; }
  .sp-card--popup{ width:100%; max-height:calc(100dvh - 24px); }
  .sp-section{ padding:48px 12px; }
  .sp-card{ padding:20px 16px 18px; border-radius:20px; }

  .sp-head{
    flex-direction:column; align-items:flex-start;
    gap:12px; margin:0 0 16px 0;
  }
  .sp-logo{ width:120px; }
  .sp-title{ font-size:30px; }
  .sp-close{ top:12px; right:12px; }

  .sp-row{
    justify-content:flex-start; align-items:center; gap:0;
    overflow-x:auto; scroll-snap-type:x mandatory;
    scrollbar-width:none; -webkit-overflow-scrolling:touch;
    margin:0 -16px;
  }
  .sp-row::-webkit-scrollbar{ display:none; }
  .sp-tile{
    flex:0 0 100%; scroll-snap-align:center;
    display:flex; justify-content:center; padding:0 16px;
  }
  .sp-frame{ height:min(50dvh, calc((100vw - 56px) * 1.25)); max-width:100%; }
  .sp-ticket{ font-size:14px; padding:10px 12px; }

  .sp-dots{ display:flex; justify-content:center; gap:8px; margin-top:14px; }
  .sp-dot{
    width:8px; height:8px; padding:0; border:none; border-radius:999px; cursor:pointer;
    background:rgba(243,239,232,.3); transition:width .25s, background .25s;
  }
  .sp-dot.is-active{ width:24px; background:var(--sp-gold); }

  .sp-foot{ margin-top:14px; padding-top:14px; gap:14px; }
  .sp-venue{ font-size:14px; }
  .sp-sponsor strong{ font-size:18px; }
}

@keyframes sp-fade{ from{opacity:0} to{opacity:1} }
@keyframes sp-fade-out{ from{opacity:1} to{opacity:0} }
@keyframes sp-rise{ from{opacity:0; transform:translateY(40px) scale(.96)} to{opacity:1; transform:none} }
@keyframes sp-drop{ from{opacity:1; transform:none} to{opacity:0; transform:translateY(30px) scale(.97)} }

@media (prefers-reduced-motion: reduce){
  .sp-overlay, .sp-card{ animation:none !important; }
  .sp-close, .sp-dot, .sp-ticket{ transition:none; }
}
`;